import React, { useState, useRef } from 'react';
import cn from 'classnames';
import { Button, TextInput, TextInputType } from '@design-system';
import { useTranslation, Trans } from 'react-i18next';
import { translations } from 'translations';

export interface AdvancedSignatureProps {
  className?: string;
  onSign: () => void;
  isLoading: boolean;
  primaryColor: string;
  handleCertificateFile: any;
  handleKeyFile: any;
  handlePassword: any;
  isDisabled: boolean;
  hideButton?: boolean;
  /** When true, reduces vertical spacing for tight layouts (e.g. mobile landscape) */
  compact?: boolean;
}

export const AdvancedSignature: React.FC<AdvancedSignatureProps> = ({
  className,
  onSign,
  isLoading = false,
  primaryColor,
  handleCertificateFile,
  handleKeyFile,
  handlePassword,
  isDisabled,
  hideButton = false,
  compact = false,
}) => {
  const { t } = useTranslation();
  const [certName, setCertName] = useState();
  const [keyName, setKeyName] = useState();
  const [selected, setSelected] = useState<any>();
  const [keyPassword, setKeyPassword] = useState('');

  const certFile: any = useRef();
  const keyFile: any = useRef();

  const handleCertificateInput = () => {
    certFile.current.click();
  };

  const handleKeyInput = () => {
    keyFile.current.click();
  };

  const handleSetCertificate = (evt) => {
    handleCertificateFile(evt);
    const currFile: File = evt.target.files[0];
    setCertName(currFile.name as any);
  };

  const handleSetKey = (evt) => {
    handleKeyFile(evt);

    const currFile: File = evt.target.files[0];
    setKeyName(currFile.name as any);
  };

  const handleSign = () => {
    if (onSign) onSign();
  };

  const handleSetPassword = (password) => {
    setKeyPassword(password);
    handlePassword(password);
  };

  // Spacing tokens that change with compact mode
  const titleClass = compact ? 'text-xs font-semibold' : 'text-subtitle';
  const boxClass = compact
    ? 'my-3 flex items-center rounded-lg border border-dashed border-gray-200 p-3 gap-3'
    : 'my-4 flex justify-center rounded-2xl border-2 border-dashed border-gray-100 p-4';
  const sectionGap = compact ? 'mb-3' : '';
  const passwordGap = compact ? 'mb-4' : 'mb-4';
  const legalClass = compact ? 'my-1 text-[10px] leading-tight text-gray-500' : 'my-2 text-sm';

  return (
    <div className={className}>
      {/* .cer upload */}
      <div className={cn('w-full', sectionGap)}>
        <p className={titleClass}>
          {t(translations.DOCUMENT_PAGES.ENTER_CER)}
        </p>
        <div className={boxClass}>
          <input
            type="file"
            id="file"
            ref={certFile}
            accept=".cer,.pem"
            onChange={handleSetCertificate}
            style={{ display: 'none' }}
          />
          <div>
            <Button
              className="mr-auto px-8 text-white"
              size="xs"
              onClick={() => handleCertificateInput()}
              color={primaryColor}
              variant="solid"
            >
              {certName
                ? t(translations.COMMON.CHANGE)
                : t(translations.COMMON.SEARCH)}
            </Button>
          </div>
          <div className="mr-auto flex px-2">
            <p className={cn('w-full', compact ? 'text-xs' : 'text-sm md:max-w-[180px]')}>
              {certName ? certName : t(translations.COMMON.NO_SELECTED_FILES)}
            </p>
          </div>
        </div>
      </div>

      {/* .key upload */}
      <div className={cn('w-full', sectionGap)}>
        <p className={titleClass}>
          {t(translations.DOCUMENT_PAGES.ENTER_KEY)}
        </p>
        <div className={boxClass}>
          <input
            type="file"
            id="file"
            ref={keyFile}
            onChange={handleSetKey}
            accept=".key,.pem"
            style={{ display: 'none' }}
          />
          <div>
            <Button
              className="mr-auto px-8 text-white"
              size="xs"
              onClick={() => handleKeyInput()}
              variant="solid"
              color={primaryColor}
            >
              {keyName
                ? t(translations.COMMON.CHANGE)
                : t(translations.COMMON.SEARCH)}
            </Button>
          </div>
          <div className="mr-auto flex px-2">
            <p className={cn('w-full', compact ? 'text-xs' : 'text-sm md:max-w-[180px]')}>
              {keyName ? keyName : t(translations.COMMON.NO_SELECTED_FILES)}
            </p>
          </div>
        </div>
      </div>

      {/* Password */}
      <div className={passwordGap}>
        <p className={cn(titleClass, 'mb-2')}>
          {t(translations.INPUTS.PASSWORD_KEY)}
        </p>
        <input
          className="w-full rounded-lg border border-gray-300 p-3 text-3xl tracking-widest placeholder:tracking-normal placeholder:text-sm focus:border-indigo-500 focus:ring-indigo-500"
          id="password"
          name="password"
          value={keyPassword}
          onChange={(e) => handleSetPassword(e.target.value)}
          type="password"
          placeholder={t(translations.INPUTS.PASSWORD_KEY)}
        />
      </div>

      {/* Legal text */}
      {/* Legal text - Hidden in compact mode (moved to sidebar) */}
      {!compact && (
        <p className={legalClass}>
          <Trans i18nKey={translations.COMMON.EFIRMA_SIGNATURE_LEGEND}>
            Al dar clic en el botón <strong>Firmar </strong>manifiestas tu
            consentimiento para que se reflejen en el documento los datos
            generales de tu certificado digital (clave pública) así como su
            vigencia.
          </Trans>
        </p>
      )}

      {!hideButton && (
        <div className="mb-4 flex">
          <div className="ml-auto">
            <Button
              className="px-8"
              size="sm"
              onClick={() => handleSign()}
              isLoading={isLoading}
              isDisabled={isDisabled}
              color={primaryColor}>
              {t(translations.COMMON.SIGN)}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
